/**
 * Two replay runs → a delta. This is the PRIMARY function of the whole
 * feature — the plan this ships from says so explicitly: a page that shows
 * one run at a time with an "analyze" button is an uploader wearing a
 * replay's name.
 *
 * Two refusals come before any comparison:
 *   - `different-input`  — two runs on different clips measure nothing about
 *     configuration; the delta would be beautiful and meaningless.
 *   - `different-node`   — two nodes are two engines (and potentially two
 *     model formats); the delta would measure hardware, not the operator's
 *     question.
 *
 * Track pairing is by `(className, time-window overlap, IoU)`, **never** by
 * an id — the tracker in `FrameProcessor` assigns fresh ids every run
 * (a fresh `SortTracker` per run, see `frame-processor-pass.ts`), so pairing
 * by id would report "everything lost, everything gained" even for two
 * byte-identical runs.
 */
export interface ReplayTrack {
  readonly className: string
  readonly firstSeenMs: number
  readonly lastSeenMs: number
  readonly bbox: { readonly x: number; readonly y: number; readonly w: number; readonly h: number }
  readonly framesMatched: number
}

export interface ReplayDiscarded {
  readonly bbox: readonly [number, number, number, number]
  readonly macroClass: string
  readonly score: number
  readonly reason: string
}

/** The subset of a replay run's verdict the diff needs — named locally so
 *  this pure module has no dependency on the store/cap layer. */
export interface ReplayRunForDiff {
  readonly clipId: string
  readonly nodeId: string
  /** The run's step settings, keyed by `addonId` then setting key — the same
   *  shape `buildReplayPlan`'s overlay produces. */
  readonly settings: Readonly<Record<string, Readonly<Record<string, unknown>>>>
  readonly tracks: readonly ReplayTrack[]
  readonly discarded: readonly ReplayDiscarded[]
}

export type ReplayDiff =
  | { readonly kind: 'incomparable'; readonly reason: 'different-input' | 'different-node' }
  | {
      readonly kind: 'compared'
      readonly changedKnobs: readonly {
        readonly stepId: string
        readonly key: string
        readonly from: unknown
        readonly to: unknown
      }[]
      readonly tracks: {
        readonly gained: readonly ReplayTrack[]
        readonly lost: readonly ReplayTrack[]
      }
      /** A box A discarded and B's tracker turned into a track — named with
       *  A's discard reason, so the operator reads "this used to be dropped
       *  by X, now it survives" instead of an unexplained new track. */
      readonly promoted: readonly {
        readonly macroClass: string
        readonly score: number
        readonly wasDiscardedBy: string
      }[]
    }

/** Fraction-of-area IoU of two pixel-space bboxes. */
function iou(
  a: { readonly x: number; readonly y: number; readonly w: number; readonly h: number },
  b: { readonly x: number; readonly y: number; readonly w: number; readonly h: number },
): number {
  const x1 = Math.max(a.x, b.x)
  const y1 = Math.max(a.y, b.y)
  const x2 = Math.min(a.x + a.w, b.x + b.w)
  const y2 = Math.min(a.y + a.h, b.y + b.h)
  const interW = Math.max(0, x2 - x1)
  const interH = Math.max(0, y2 - y1)
  const inter = interW * interH
  if (inter <= 0) return 0
  const union = a.w * a.h + b.w * b.h - inter
  return union <= 0 ? 0 : inter / union
}

function windowsOverlap(
  a: { readonly firstSeenMs: number; readonly lastSeenMs: number },
  b: { readonly firstSeenMs: number; readonly lastSeenMs: number },
): boolean {
  return a.firstSeenMs <= b.lastSeenMs && b.firstSeenMs <= a.lastSeenMs
}

/** Minimum IoU (on each pair's representative bbox) for two tracks across
 *  runs to count as "the same subject", once class and time window already
 *  agree. Deliberately generous — the tracker's own spawn jitter between two
 *  runs of the SAME input can shift a box by a few px; the number this
 *  guards against is "a different subject happened to be nearby", not
 *  sub-pixel replay noise. */
const TRACK_PAIR_MIN_IOU = 0.3

/** Pair tracks across two runs by (className, window overlap, IoU) — a
 *  greedy best-match, since a replay clip rarely has enough simultaneous
 *  same-class subjects for greedy vs. optimal assignment to matter. Returns
 *  the tracks of `a` with no match in `b` (lost) and vice versa (gained). */
function diffTracks(
  a: readonly ReplayTrack[],
  b: readonly ReplayTrack[],
): { readonly gained: readonly ReplayTrack[]; readonly lost: readonly ReplayTrack[] } {
  const bMatched = new Set<number>()
  const lost: ReplayTrack[] = []
  for (const ta of a) {
    let bestIdx = -1
    let bestIou = 0
    for (let i = 0; i < b.length; i++) {
      if (bMatched.has(i)) continue
      const tb = b[i]
      if (tb === undefined || tb.className !== ta.className) continue
      if (!windowsOverlap(ta, tb)) continue
      const score = iou(ta.bbox, tb.bbox)
      if (score >= TRACK_PAIR_MIN_IOU && score > bestIou) {
        bestIou = score
        bestIdx = i
      }
    }
    if (bestIdx === -1) lost.push(ta)
    else bMatched.add(bestIdx)
  }
  const gained = b.filter((_, i) => !bMatched.has(i))
  return { gained, lost }
}

function diffKnobs(
  a: ReplayRunForDiff['settings'],
  b: ReplayRunForDiff['settings'],
): readonly {
  readonly stepId: string
  readonly key: string
  readonly from: unknown
  readonly to: unknown
}[] {
  const stepIds = new Set([...Object.keys(a), ...Object.keys(b)])
  const changed: { stepId: string; key: string; from: unknown; to: unknown }[] = []
  for (const stepId of stepIds) {
    const aStep = a[stepId] ?? {}
    const bStep = b[stepId] ?? {}
    const keys = new Set([...Object.keys(aStep), ...Object.keys(bStep)])
    for (const key of keys) {
      const from = aStep[key]
      const to = bStep[key]
      if (from !== to) changed.push({ stepId, key, from, to })
    }
  }
  return changed
}

/** A discarded box that A dropped and B's tracks now cover, keyed by
 *  `(macroClass, score)` — a replay's discard/track fixtures are exact, so
 *  an exact match is enough to name the promotion without inventing a
 *  spatial matcher for a case the plan's own tests never exercise
 *  positionally. */
function diffPromoted(
  discardedA: readonly ReplayDiscarded[],
  tracksB: readonly ReplayTrack[],
): readonly {
  readonly macroClass: string
  readonly score: number
  readonly wasDiscardedBy: string
}[] {
  const promoted: { macroClass: string; score: number; wasDiscardedBy: string }[] = []
  for (const d of discardedA) {
    const nowTracked = tracksB.some((t) => t.className === d.macroClass)
    if (nowTracked)
      promoted.push({ macroClass: d.macroClass, score: d.score, wasDiscardedBy: d.reason })
  }
  return promoted
}

export function diffReplayRuns(a: ReplayRunForDiff, b: ReplayRunForDiff): ReplayDiff {
  if (a.clipId !== b.clipId) return { kind: 'incomparable', reason: 'different-input' }
  if (a.nodeId !== b.nodeId) return { kind: 'incomparable', reason: 'different-node' }
  return {
    kind: 'compared',
    changedKnobs: diffKnobs(a.settings, b.settings),
    tracks: diffTracks(a.tracks, b.tracks),
    promoted: diffPromoted(a.discarded, b.tracks),
  }
}
